import axios from "../general/HttpClient";

// Managed Service Types
export enum ManagedServiceType {
    Link = 0,
    SASAMS = 1,
    Scadsys = 2,
    Store = 3,
    ScadsysBiometrics = 4,
    Azure = 5,
    Deprecated = 6,
    SendGrid = 7,
    Discord = 8,
    Sage300 = 9,
    ScadsysAssets = 10,
    AppWebsite = 11
}

// Generic Managed Service interface with configuration type parameter
export interface ManagedService<TConfig = any> {
    id: string;
    organizationID: string;
    type: ManagedServiceType;
    name?: string;
    configuration: TConfig;
}

// Specific configuration types
export interface ScadsysConfiguration {
    onlineSchoolID?: string;
    webServiceUrl?: string;
    webServiceReference?: string;
    institutionName?: string;
    storageAccountName?: string;
    fileSystemName?: string;
    organizationFolder?: string;
    servicePrincipalAppId?: string;
    servicePrincipalSecret?: string;
    servicePrincipalId?: string;
    tenantId?: string;
    syncTimetables?: boolean;
}

export interface AzureConfiguration {
    tenantID?: string;
    subscriptionId?: string;
    [key: string]: any;
}

export interface SendGridConfiguration {
    apiKey?: string;
    fromEmail?: string;
    fromName?: string;
    [key: string]: any;
}

export interface ScadsysAssetsConfiguration {
    assetEntityTypeID: string;
    assetCategoryScopeTypeID: string;
}

export interface AppWebsiteScreenshot {
    url: string;
    imageKey: string;
    title: string;
    caption: string;
}

export interface AppWebsiteConfiguration {
    domain: string;
    aliases: string[];
    published: boolean;
    appName: string;
    school: string;
    shortName: string;
    tagline: string;
    schoolWebsite: string;
    supportEmail: string;
    androidPackage: string;
    iosBundleId: string;
    multiSchool: boolean;
    themePrefix: string;
    logoImageKey: string;
    iconImageKey: string;
    logoUrl: string;
    iconUrl: string;
    faviconUrl: string;
    palette: { primary: string; secondary: string; deep: string };
    stores: { android: { status: string; url: string }; ios: { status: string; url: string } };
    screenshots: AppWebsiteScreenshot[];
    heroScreenshot: number;
    revision?: string;
}

export function UploadAppWebsiteImage(organizationId: string, serviceId: string, file: File) {
    const data = new FormData();
    data.append("file", file);
    return axios.post<{ url: string }>(
        "/api/app-websites/" + organizationId + "/" + serviceId + "/images", data);
}

// Managed service with specific configuration types
export type ScadsysManagedService = ManagedService<ScadsysConfiguration>;
export type AzureManagedService = ManagedService<AzureConfiguration>;
export type SendGridManagedService = ManagedService<SendGridConfiguration>;
export type ScadsysAssetsManagedService = ManagedService<ScadsysAssetsConfiguration>;

export function ListManagedServices(organizationId: string) {
    return axios.get<ManagedService[]>(`/api/managedServices/ListManagedServices/${organizationId}`);
}

export function CreateManagedService<TConfig = any>(organizationId: string, managedService: Partial<ManagedService<TConfig>>) {
    return axios.post<ManagedService<TConfig>>(`/api/managedServices/CreateManagedService/${organizationId}`, managedService);
}

export function UpdateManagedService<TConfig = any>(organizationId: string, managedService: ManagedService<TConfig>) {
    return axios.put<ManagedService<TConfig>>(`/api/managedServices/UpdateManagedService/${organizationId}`, managedService);
}

export function DeleteManagedService(organizationId: string, managedServiceId: string) {
    return axios.delete(`/api/managedServices/DeleteManagedService/${organizationId}`, { params: { managedServiceId } });
}

export function TypeName(type: ManagedServiceType | number): string {
    switch (type) {
        case ManagedServiceType.AppWebsite:
            return "App Website";
        case ManagedServiceType.Link:
            return "Link";
        case ManagedServiceType.SASAMS:
            return "SASAMS";
        case ManagedServiceType.Scadsys:
            return "Scadsys";
        case ManagedServiceType.Store:
            return "Store";
        case ManagedServiceType.ScadsysBiometrics:
            return "Scadsys Biometrics";
        case ManagedServiceType.Azure:
            return "Azure";
        case ManagedServiceType.Deprecated:
            return "Deprecated";
        case ManagedServiceType.SendGrid:
            return "SendGrid";
        case ManagedServiceType.Discord:
            return "Discord";
        case ManagedServiceType.Sage300:
            return "Sage300";
        case ManagedServiceType.ScadsysAssets:
            return "Scadsys Assets";
    }
    return "Unknown";
}
